import { Light } from './lights/Light.js';
import { ColorMode } from './debug_visualizers/LightVisualizer.js';
import { assert } from '../../utility/Assert.js';
/**
 * A system for managing lights in the viewer.
 * Contains the lights, shadow casters and functionality for visualizing lights
 */
export class LightSystem {
    constructor(max_shadowcasting_lights, viewer) {
        this.max_shadowcasting_lights = max_shadowcasting_lights;
        this.viewer = viewer;
        this._lights = [];
        this._shadow_casters = new Set();
        this._dirty_meshes_bounds = new Map();
        this._debug_enabled = false;
        this._debug_color_mode = ColorMode.DIFFERENTIATED;
        this._forced_dirty = false;
        this._object_removed_observer =
            this.viewer.viewer_scene.object_removed_observable
                .add((_, object) => this.on_object_removed(object));
    }
    add_light(light) {
        if (this.debug_enabled) {
            assert(light.debug_geomentry === null, 'Light already has debug geometry');
            light.create_debug_geometry();
        }
        this._lights.push(light);
    }
    remove_light(light) {
        if (!this._lights.includes(light)) {
            console.warn(`Not able to remove light with id: ${light.id}. Light not found`);
            return false;
        }
        this._lights.splice(this._lights.indexOf(light), 1);
        this._forced_dirty = true;
        return true;
    }
    has_light(light) {
        return this._lights.includes(light);
    }
    get_all_lights() {
        return [...this._lights];
    }
    dispose_all_lights() {
        this._lights.forEach(light => light.dispose());
        this._lights.length = 0;
        this._forced_dirty = true;
    }
    mark_all_shadowmaps_dirty() {
        this._lights.forEach((light) => {
            var _a;
            if (light.shadow !== null) {
                (_a = light.shadow) === null || _a === void 0 ? void 0 : _a.update_babylon_shadowmap();
                light.mark_dirty(Light.DIRTY_SHADOW_MAP);
            }
        });
    }
    update() {
        const meshes_bounds_modifications = [...this._dirty_meshes_bounds.values()];
        this._lights.forEach(light => {
            var _a, _b, _c;
            light.update();
            (_a = light.shadow) === null || _a === void 0 ? void 0 : _a.update(meshes_bounds_modifications);
            (_b = light.shadow) === null || _b === void 0 ? void 0 : _b.render();
            if (light.is_dirty()) {
                light.update_observable.notify(light);
                (_c = light.debug_geomentry) === null || _c === void 0 ? void 0 : _c.update();
                this.viewer.mark_scene_dirty();
            }
            light.clear_dirty_flags();
        });
        this._dirty_meshes_bounds.clear();
        this._forced_dirty = false;
    }
    set_cast_shadow(object, cast_shadow) {
        if (cast_shadow) {
            this._shadow_casters.add(object);
            // This is a quick fix. We should have a better solution for adding shadow casters. TODO: IC3V-1677
            this._lights.forEach(light => { var _a; return (_a = light.shadow) === null || _a === void 0 ? void 0 : _a.add_shadowcaster(object); });
        }
        else {
            this._shadow_casters.delete(object);
            this._lights.forEach(light => { var _a; return (_a = light.shadow) === null || _a === void 0 ? void 0 : _a.remove_shadowcaster(object); });
        }
    }
    can_enable_shadow() {
        const shadowcasting_lights = this.viewer.scene.lights.filter((light) => light.shadowEnabled);
        return shadowcasting_lights.length < this.max_shadowcasting_lights;
    }
    get debug_enabled() {
        return this._debug_enabled;
    }
    set debug_enabled(debug) {
        this._debug_enabled = debug;
        this._lights.forEach(light => {
            if (debug) {
                assert(light.debug_geomentry === null, 'Light already has debug geometry');
                light.create_debug_geometry();
            }
            else {
                light.remove_debug_geometry();
            }
        });
    }
    set debug_color_mode(mode) {
        this._debug_color_mode = mode;
        this._lights.forEach(light => {
            const debug_geomentry = light.debug_geomentry;
            if (debug_geomentry) {
                debug_geomentry.color_mode = mode;
            }
        });
    }
    get debug_color_mode() {
        return this._debug_color_mode;
    }
    is_dirty() {
        return this._lights.some(light => light.is_dirty()) || this._forced_dirty;
    }
    get ready() {
        return Promise.all(this._lights.map(light => light.ready)).then(() => Promise.resolve());
    }
    dispose() {
        this.dispose_all_lights();
        this._shadow_casters.clear();
        this._dirty_meshes_bounds.clear();
        this.viewer.viewer_scene.object_removed_observable.remove(this._object_removed_observer);
    }
    get shadow_casters() {
        return this._shadow_casters;
    }
    /**
     * Notify the light system that a mesh has been modified.
     */
    on_mesh_modified(mesh_id, old_bounds, new_bounds) {
        if (this._dirty_meshes_bounds.has(mesh_id)) {
            // If we modify an object multiple times within a frame, we only want to process the composed result.
            // E.g. we only need to know where the object was located the previous frame, and where it will be located next frame.
            this._dirty_meshes_bounds.get(mesh_id).new_bounds = new_bounds;
            // TODO we never updat old bounds? Why we need them?
        }
        else {
            this._dirty_meshes_bounds.set(mesh_id, { old_bounds: old_bounds, new_bounds: new_bounds });
        }
    }
    /**
     * Notify the light system that a scene object has been modified.
     */
    on_object_modified(scene_object) {
        this._lights.forEach(light => {
            if (light.parent === scene_object.id) {
                light.mark_dirty(Light.DIRTY_PARENT_TRANSFORM);
            }
        });
    }
    on_object_removed(object) {
        this._lights.forEach(light => {
            if (light.parent === object.id) {
                light.mark_dirty(Light.DIRTY_PARENT_REMOVED);
            }
        });
        this._shadow_casters.delete(object);
    }
}
LightSystem.BABYLON_MATERIAL_MAXIMUM_LIGHT_COUNT = 64;
//# sourceMappingURL=LightSystem.js.map